/**
 * Alta y edición de un **activo** de mantenimiento.
 *
 * Puerto del formulario modal de `views/maintenance/assets/index.php`: los
 * mismos campos (categoría, código, nombre, serie, modelo, ubicación, fechas,
 * estado y descripción), con el código en mayúsculas como el panel anterior.
 */

import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type { Activo, ActivoPayload, CategoriaDeActivo } from '../activos.types';

/** Estilo de los controles nativos (mismo que el resto del panel). */
const CONTROL =
  'h-12 w-full rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const MAX_CODIGO = 50;
const MAX_NOMBRE = 150;

/** Etiqueta de un campo que no usa el componente `Input`. */
function Etiqueta({ htmlFor, children }: { htmlFor: string; children: string }) {
  return (
    <label htmlFor={htmlFor} className="text-sm font-medium text-ink-700">
      {children}
    </label>
  );
}

export function ActivoDialogo({
  activo,
  categorias,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  activo: Activo | null;
  categorias: CategoriaDeActivo[];
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: ActivoPayload) => void;
  onCerrar: () => void;
}) {
  const [categoriaId, setCategoriaId] = useState(
    activo?.categoria_id ?? categorias.find((categoria) => categoria.activa)?.id ?? 0,
  );
  const [codigo, setCodigo] = useState(activo?.codigo ?? '');
  const [nombre, setNombre] = useState(activo?.nombre ?? '');
  const [serie, setSerie] = useState(activo?.serie ?? '');
  const [modelo, setModelo] = useState(activo?.modelo ?? '');
  const [ubicacion, setUbicacion] = useState(activo?.ubicacion ?? '');
  const [instalacion, setInstalacion] = useState(activo?.instalacion ?? '');
  const [garantia, setGarantia] = useState(activo?.garantia ?? '');
  const [descripcion, setDescripcion] = useState(activo?.descripcion ?? '');
  const [estado, setEstado] = useState<'activo' | 'desuso'>(
    activo ? (activo.activo ? 'activo' : 'desuso') : 'activo',
  );

  const codigoLimpio = codigo.trim().toUpperCase();
  const nombreLimpio = nombre.trim();

  // Sólo se ofrecen categorías activas (más la del activo si quedó inactiva).
  const opciones = categorias.filter(
    (categoria) => categoria.activa || categoria.id === activo?.categoria_id,
  );

  const errorCodigo =
    codigoLimpio.length === 0
      ? 'El código es obligatorio.'
      : codigoLimpio.length > MAX_CODIGO
        ? `Máximo ${MAX_CODIGO} caracteres.`
        : null;
  const errorNombre =
    nombreLimpio.length === 0
      ? 'El nombre es obligatorio.'
      : nombreLimpio.length > MAX_NOMBRE
        ? `Máximo ${MAX_NOMBRE} caracteres.`
        : null;

  const valido =
    categoriaId > 0 && !(codigoLimpio.length > 0 && errorCodigo) && !(nombreLimpio.length > 0 && errorNombre);

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="lg"
      title={activo ? 'Editar activo' : 'Nuevo activo'}
      description={
        activo
          ? 'Los sub-activos y el historial del activo se conservan.'
          : 'Registra un equipo para poder colgarle sub-activos y tareas de mantenimiento.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                categoria_id: categoriaId,
                codigo: codigoLimpio,
                nombre: nombreLimpio,
                serie: serie.trim(),
                descripcion: descripcion.trim(),
                ubicacion: ubicacion.trim(),
                modelo: modelo.trim(),
                instalacion,
                garantia,
                activo: estado === 'activo',
              })
            }
          >
            {activo ? 'Guardar cambios' : 'Crear activo'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <Alert variant="error" title="No se pudo guardar">
            {error}
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="activo-categoria">Categoría</Etiqueta>
            <Select
              id="activo-categoria"
              className={CONTROL}
              value={String(categoriaId)}
              onChange={(evento) => setCategoriaId(Number(evento.target.value))}
            >
              {opciones.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                  {categoria.activa ? '' : ' (inactiva)'}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="activo-estado">Estado</Etiqueta>
            <Select
              id="activo-estado"
              className={CONTROL}
              value={estado}
              onChange={(evento) => setEstado(evento.target.value === 'desuso' ? 'desuso' : 'activo')}
            >
              <option value="activo">Activo</option>
              <option value="desuso">En desuso</option>
            </Select>
          </div>

          <Input
            label="Código"
            value={codigo}
            maxLength={MAX_CODIGO}
            placeholder="Ej. DISP-01"
            className="font-mono uppercase"
            error={codigoLimpio.length > 0 ? (errorCodigo ?? undefined) : undefined}
            hint={codigoLimpio.length === 0 ? 'Hasta 50 caracteres, sin repetir otro activo' : undefined}
            onChange={(evento) => setCodigo(evento.target.value)}
          />

          <Input
            label="Número de serie"
            value={serie}
            maxLength={100}
            placeholder="Opcional, sin repetir"
            onChange={(evento) => setSerie(evento.target.value)}
          />

          <Input
            label="Nombre"
            value={nombre}
            maxLength={MAX_NOMBRE}
            placeholder="Ej. Dispensador isla 1"
            error={nombreLimpio.length > 0 ? (errorNombre ?? undefined) : undefined}
            hint={nombreLimpio.length === 0 ? 'Hasta 150 caracteres' : undefined}
            onChange={(evento) => setNombre(evento.target.value)}
          />

          <Input
            label="Modelo"
            value={modelo}
            maxLength={100}
            placeholder="Opcional"
            onChange={(evento) => setModelo(evento.target.value)}
          />

          <Input
            label="Ubicación"
            value={ubicacion}
            maxLength={150}
            placeholder="Ej. Isla 1"
            onChange={(evento) => setUbicacion(evento.target.value)}
          />

          <Input
            label="Fecha de instalación"
            type="date"
            value={instalacion}
            onChange={(evento) => setInstalacion(evento.target.value)}
          />

          <Input
            label="Vencimiento de garantía"
            type="date"
            value={garantia}
            onChange={(evento) => setGarantia(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Etiqueta htmlFor="activo-descripcion">Descripción</Etiqueta>
          <textarea
            id="activo-descripcion"
            rows={3}
            value={descripcion}
            placeholder="Detalles adicionales del equipo"
            className="w-full rounded-control border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 outline-none transition-all duration-200 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

export { CONTROL as CONTROL_ACTIVO };
